<template>
    <!-- Main Container with Header Style -->
    <div class="bg-white rounded-lg shadow-[6px_4px_0_0_rgba(0,0,0,1)] border-2 border-black mb-8">
      <!-- Header Section - Matching Upcoming Sessions style -->
      <div class="bg-blue-50 p-3 sm:p-4 md:p-6 rounded-t-lg">
        <div class="flex items-center mb-1">
          <div class="w-8 h-8 rounded-lg flex items-center justify-start">
            <svg
              class="w-6 h-6 text-blue-800"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h2 class="text-2xl font-semibold text-blue-900">Manage Availability</h2>
        </div>
        <p class="text-blue-800 font-semibold">
          Set your available time slots for tutoring sessions.
        </p>
      </div>

      <div class="p-3 sm:p-4 md:p-6">
        
        <!-- Info Banner - No Availability Set -->
        <div v-if="!hasAvailability" class="mb-6 p-4 bg-amber-50 border-l-4 border-amber-400 rounded-lg">
          <div class="flex items-start gap-3">
            <svg class="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <h4 class="text-sm font-semibold text-amber-800 mb-1">Set Your Availability to Get Started</h4>
              <p class="text-sm text-amber-700">
                You won't appear in student searches until you add at least one time slot. Click "Add Time Slot" to make yourself available for tutoring sessions.
              </p>
            </div>
          </div>
        </div>

        <!-- Weekly Schedule Section -->
        <div class="mb-8">
          <div class="flex items-center justify-between mb-6">
            <h3 class="text-xl font-semibold text-gray-900">Weekly Schedule</h3>
            <button
              @click="addTimeSlot"
              class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium flex items-center gap-2 cursor-pointer"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 4v16m8-8H4"
                />
              </svg>
              Add Time Slot
            </button>
          </div>

          <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <!-- Day Cards -->
            <div
              v-for="day in daysOfWeek"
              :key="day"
              class="bg-white border-2 border-black shadow-[4px_4px_0_0_rgba(0,0,0,1)] rounded-lg"
            >

              <div class="flex items-center justify-between bg-gray-100 rounded-t-lg mb-3 border-b-2 border-black pb-2 p-4">
                <h4 class="font-semibold text-gray-900">{{ day }}</h4>
              </div>
              <div class=" p-4">
              <div v-if="getAvailabilityForDay(day).length > 0" class="space-y-3">
                <div
                  v-for="(slot, index) in getAvailabilityForDay(day)"
                  :key="index"
                  class="flex flex-col sm:flex-row sm:items-center justify-between p-3 bg-blue-50/50 border border-blue-800 rounded-lg gap-3"
                >
                  <div class="flex flex-col sm:flex-row sm:items-center gap-3 min-w-0 flex-1">
                    <div class="flex items-center gap-2 flex-shrink-0">
                      <svg
                        class="w-4 h-4 text-blue-700 flex-shrink-0"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <span class="text-sm font-medium text-blue-800 whitespace-nowrap">
                        {{ formatTime(slot.startTime) }} - {{ formatTime(slot.endTime) }}
                      </span>
                    </div>
                    <div class="flex flex-wrap gap-2">
                      <span
                        v-if="slot.mode === 'face-to-face' || slot.mode === 'both'"
                        class="px-2 py-1 text-xs font-medium rounded-full bg-orange-100 text-orange-800 border border-orange-200 whitespace-nowrap"
                      >
                        Face-to-Face
                      </span>
                      <span
                        v-if="slot.mode === 'online' || slot.mode === 'both'"
                        class="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800 border border-blue-200 whitespace-nowrap"
                      >
                        Online
                      </span>
                    </div>
                  </div>
                  <button
                    @click="removeTimeSlot(day, index)"
                    class="px-3 py-1 bg-white text-red-600 border border-red-600 rounded-md hover:bg-red-600 hover:text-white transition-colors text-sm cursor-pointer whitespace-nowrap flex-shrink-0"
                  >
                    Remove
                  </button>
                </div>
              </div>
              <div v-else class="text-center py-4">
                <p class="text-gray-500 text-sm italic">Not available</p>
              </div>
            </div>
          </div>
          </div>
        </div>



        <!-- Availability Tips -->
        <div class="bg-orange-50 border border-orange-200 rounded-lg p-4">
          <div class="flex items-center gap-2 mb-3">
            <svg
              class="w-5 h-5 text-orange-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <h4 class="font-semibold text-orange-800">Availability Tips</h4>
          </div>
          <ul class="text-sm text-orange-700 space-y-1">
            <li>• Each time slot is automatically set to 1 hour maximum</li>
            <li>• Set consistent time slots to help students plan their sessions</li>
            <li>• Update your availability at least 24 hours in advance</li>
            <li>• Consider peak study hours (after school and weekends)</li>
            <li>• Leave buffer time between sessions for preparation</li>
          </ul>
        </div>
      </div>
    </div>

    <!-- Add Time Slot Modal -->
    <transition name="modal-backdrop" appear>
      <div v-if="showModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999]">
        <transition name="modal" appear>
          <div class="bg-white border-2 border-black shadow-[6px_6px_0_0_rgba(0,0,0,1)] rounded-lg p-6 w-full max-w-md mx-4 modal-content">
            <div class="flex items-center justify-between mb-4">
              <h3 class="text-lg font-semibold text-gray-900">Add New Time Slot</h3>
              <button @click="closeModal" class="text-gray-400 hover:text-gray-600 transition-colors">
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
          </button>
        </div>

        <form @submit.prevent="submitTimeSlot" class="space-y-4">
          <!-- Day Selection -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">Day</label>
            <select
              v-model="newSlot.dayOfWeek"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            >
              <option value="">Select a day</option>
              <option v-for="day in daysOfWeek" :key="day" :value="day">{{ day }}</option>
            </select>
          </div>

          <!-- Time Selection with 1-hour constraint -->
          <div class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Start Time</label>
              <input
                type="time"
                v-model="newSlot.startTime"
                @input="calculateEndTime"
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>
            
            <!-- Auto-calculated End Time (Read-only) -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">
                End Time 
              </label>
              <input
                type="time"
                v-model="newSlot.endTime"
                readonly
                class="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600 cursor-not-allowed"
              />
            </div>
            
            
          </div>

          <!-- Mode Selection -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">Mode</label>
            <select
              v-model="newSlot.mode"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            >
              <option value="">Select mode</option>
              <option 
                v-for="modeOption in availableModeOptions" 
                :key="modeOption.value" 
                :value="modeOption.value"
              >
                {{ modeOption.label }}
              </option>
            </select>
            
            <!-- Clearance info message -->
            <div v-if="!hasClearance" class="mt-2 p-3 bg-orange-50 border border-orange-200 rounded-lg">
              <div class="flex items-start gap-2">
                <svg class="w-4 h-4 text-orange-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p class="text-xs text-orange-800">
                  {{ clearanceMessage }}
                </p>
              </div>
            </div>
          </div>

          <!-- Action Buttons -->
          <div class="flex gap-3 pt-4">
            <button
              type="button"
              @click="closeModal"
              class="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              class="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              Add Slot
            </button>
          </div>
        </form>
          </div>
        </transition>
      </div>
    </transition>

    <!-- Notification Popup with Animation -->
    <transition name="backdrop" appear>
      <div v-if="showNotification" class="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999]">
        <transition name="notification" appear>
          <div class="bg-white rounded-lg p-6 border-2 border-black shadow-[6px_6px_0_0_rgba(0,0,0,1)] w-full max-w-md mx-4 notification-popup">
            <div class="text-center">
              <!-- Success Icon with Animation -->
              <div v-if="messageType === 'success'" class="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4 success-icon">
                <svg class="h-8 w-8 text-green-600 checkmark" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"></path>
                </svg>
              </div>
              
              <!-- Error Icon with Animation -->
              <div v-else class="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-4 error-icon">
                <svg class="h-8 w-8 text-red-600 cross" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </div>

          <!-- Title -->
          <h3 :class="[
            'text-lg font-medium mb-2',
            messageType === 'success' ? 'text-green-900' : 'text-red-900'
          ]">
            {{ messageType === 'success' ? 'Success!' : 'Error' }}
          </h3>

          <!-- Message -->
          <p :class="[
            'text-sm mb-6',
            messageType === 'success' ? 'text-green-700' : 'text-red-700'
          ]">
            {{ message }}
          </p>

          <!-- Close Button -->
          <button
            @click="closeNotification"
            :class="[
              'w-full px-4 py-2 rounded-md font-medium transition-colors',
              messageType === 'success' 
                ? 'bg-green-600 hover:bg-green-700 text-white' 
                : 'bg-red-600 hover:bg-red-700 text-white'
            ]"
          >
            OK
          </button>
        </div>
          </div>
        </transition>
      </div>
    </transition>

    <!-- Delete Confirmation Dialog -->
    <transition name="backdrop" appear>
      <div v-if="showDeleteConfirmation" class="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999]">
        <transition name="notification" appear>
          <div class="bg-white rounded-lg p-6 border-2 border-black shadow-[6px_6px_0_0_rgba(0,0,0,1)] w-full max-w-md mx-4">
            <!-- Header -->
            <div class="flex items-center gap-3 mb-4">
              <div class="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <ExclamationTriangleIcon class="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 class="text-lg font-semibold text-gray-900">Delete Availability Slot?</h3>
                <p class="text-sm text-gray-600">This action cannot be undone</p>
              </div>
            </div>

            <!-- Slot Info -->
            <div v-if="slotToDelete" class="mb-4">
              <div class="bg-gray-50 rounded-lg p-3 border">
                <div class="flex items-center justify-between">
                  <div>
                    <p class="font-medium text-gray-900">{{ slotToDelete.slot.dayOfWeek }}</p>
                    <p class="text-sm text-gray-600">
                      {{ formatTime(slotToDelete.slot.startTime) }} - {{ formatTime(slotToDelete.slot.endTime) }}
                    </p>
                  </div>
                  <span class="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
                    {{ slotToDelete.slot.mode === 'both' ? 'Both' : 
                       slotToDelete.slot.mode === 'face-to-face' ? 'F2F' : 'Online' }}
                  </span>
                </div>
              </div>
              
              <!-- Simple warning if sessions exist -->
              <div v-if="slotToDelete.hasExistingSessions" class="mt-3 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                <div class="flex items-start gap-2">
                  <ExclamationTriangleIcon class="w-4 h-4 text-orange-600 mt-0.5 flex-shrink-0" />
                  <p class="text-sm text-orange-800">
                    <strong>Note:</strong> You may have existing sessions during this time. They will remain scheduled.
                  </p>
                </div>
              </div>
            </div>

            <!-- Action Buttons -->
            <div class="flex gap-3">
              <button
                @click="closeDeleteConfirmation"
                class="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                @click="confirmDelete"
                class="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </transition>
      </div>
    </transition>

    <!-- Google Calendar Required Modal (Non-Dismissible) -->
    <transition name="modal-backdrop" appear>
      <div v-if="showGoogleCalendarRequiredModal" class="fixed inset-0 bg-black/60 flex items-center justify-center z-[9999]">
        <transition name="modal" appear>
          <div class="bg-white border-2 border-black shadow-[6px_6px_0_0_rgba(0,0,0,1)] rounded-lg p-6 w-full max-w-md mx-4">
            <div class="text-center mb-6">
              <div class="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                <svg class="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 class="text-xl font-bold text-gray-900 mb-2">Google Calendar Required</h3>
              <p class="text-gray-600 mb-4">
                You need to connect your Google Calendar before setting availability. This ensures your tutoring sessions are automatically synced to your calendar.
              </p>
            </div>

            <div>
              <button
                @click="connectToGoogleCalendar"
                class="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                Connect Google Calendar Now
              </button>
            </div>
          </div>
        </transition>
      </div>
    </transition>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { useTutorAvailabilityStore } from '@/composables/useTutorAvailabilityStore';
import { auth } from '@/firebase';
import { ExclamationTriangleIcon } from '@heroicons/vue/24/outline';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '@/firebase';
import { hasClearanceDocument, getClearanceMessage } from '@/utils/tutorClearanceCheck';
import { isGoogleCalendarConnected } from '@/services/googleCalendarApi';
import { logDebug, logError } from '@/utils/secureLogger';

interface AvailabilitySlot {
  dayOfWeek: string;
  startTime: string;
  endTime: string;
  mode: 'online' | 'face-to-face' | 'both';
}

// Store and reactive data
const { addSingleSlot, removeSingleSlot, getAvailability } = useTutorAvailabilityStore();

const availability = ref<AvailabilitySlot[]>([]);
const message = ref('');
const messageType = ref<'success' | 'error'>('success');
const showNotification = ref(false);
const showDeleteConfirmation = ref(false);
const slotToDelete = ref<{ day: string; index: number; slot: AvailabilitySlot; hasExistingSessions: boolean } | null>(null);
const isLoadingAvailability = ref(true);

// Clearance check
const hasClearance = ref(false);
const clearanceMessage = ref('');

// Google Calendar connection check
const isGoogleCalendarLinked = ref(false);
const showGoogleCalendarRequiredModal = ref(false);

const daysOfWeek = [
  'Monday',
  'Tuesday', 
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday'
];

// Modal state
const showModal = ref(false);
const newSlot = ref({
  dayOfWeek: '',
  startTime: '09:00',
  endTime: '10:00',
  mode: 'online' as 'online' | 'face-to-face' | 'both'
});

// Computed properties
const hasAvailability = computed(() => {
  // Don't show the banner while loading to prevent flash
  if (isLoadingAvailability.value) return true;
  return availability.value.length > 0;
});

// Available mode options based on clearance status
const availableModeOptions = computed(() => {
  const modes = [
    { value: 'online', label: 'Online' }
  ];
  
  // Only allow face-to-face modes if tutor has clearance
  if (hasClearance.value) {
    modes.push(
      { value: 'face-to-face', label: 'Face-to-Face' },
      { value: 'both', label: 'Both Online & Face-to-Face' }
    );
  }
  
  return modes;
});

// Check tutor's clearance status
const checkClearanceStatus = async () => {
  if (!auth.currentUser) return;
  
  try {
    hasClearance.value = await hasClearanceDocument(auth.currentUser.uid);
    clearanceMessage.value = getClearanceMessage(hasClearance.value);
  } catch (error) {
    logError('Error checking clearance status', error instanceof Error ? error : new Error(String(error)));
    hasClearance.value = false;
    clearanceMessage.value = 'Unable to verify clearance status.';
  }
};

// Check Google Calendar connection status
const checkGoogleCalendarConnection = async () => {
  if (!auth.currentUser) return;
  
  try {
    isGoogleCalendarLinked.value = await isGoogleCalendarConnected(auth.currentUser.uid);
    logDebug('Google Calendar connection status updated', {
      isLinked: isGoogleCalendarLinked.value,
    });
  } catch (error) {
    logError(
      'Error checking Google Calendar connection',
      error instanceof Error ? error : new Error(String(error))
    );
    isGoogleCalendarLinked.value = false;
  }
};

// Helper functions
const getAvailabilityForDay = (day: string): AvailabilitySlot[] => {
  return availability.value
    .filter(slot => slot.dayOfWeek === day)
    .sort((a, b) => {
      // Convert time strings to comparable numbers (e.g., "09:00" -> 900, "14:30" -> 1430)
      const timeToNumber = (time: string) => {
        const [hours, minutes] = time.split(':').map(Number);
        return hours * 100 + minutes;
      };
      return timeToNumber(a.startTime) - timeToNumber(b.startTime);
    });
};

const formatTime = (time: string): string => {
  const [hours, minutes] = time.split(':');
  const hour24 = parseInt(hours);
  const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
  const ampm = hour24 >= 12 ? 'PM' : 'AM';
  return `${hour12}:${minutes} ${ampm}`;
};

// Auto-calculate end time (1 hour after start time)
const calculateEndTime = () => {
  if (!newSlot.value.startTime) return;
  
  const [hours, minutes] = newSlot.value.startTime.split(':');
  const startDate = new Date();
  startDate.setHours(parseInt(hours), parseInt(minutes), 0, 0);
  
  // Add 1 hour (60 minutes)
  const endDate = new Date(startDate.getTime() + 60 * 60 * 1000);
  
  // Format as HH:MM
  const endHours = endDate.getHours().toString().padStart(2, '0');
  const endMinutes = endDate.getMinutes().toString().padStart(2, '0');
  
  newSlot.value.endTime = `${endHours}:${endMinutes}`;
};

// Check for existing sessions in this availability slot
const checkForExistingSessions = async (slot: AvailabilitySlot): Promise<boolean> => {
  if (!auth.currentUser) return false;
  
  try {
    // Query sessions for this tutor that might overlap with this availability slot
    const sessionsRef = collection(db, 'sessions');
    const sessionsQuery = query(
      sessionsRef,
      where('tutorId', '==', auth.currentUser.uid),
      where('status', '==', 'active'),
      where('sessionStatus', 'in', ['pending', 'confirmed', 'in-progress'])
    );
    
    const sessionsSnapshot = await getDocs(sessionsQuery);
    
    if (sessionsSnapshot.empty) return false;
    
    // Check if any sessions overlap with this availability slot
    // This is a simplified check - in a real implementation, you'd want to check
    // if sessions are scheduled on days that match this availability slot
    return sessionsSnapshot.docs.length > 0;
    
  } catch (error) {
    logError(
      'Error checking for existing sessions',
      error instanceof Error ? error : new Error(String(error))
    );
    return false; // Assume no sessions if we can't check
  }
};

// Check for duplicate or overlapping time slots
const checkForDuplicateOrOverlap = (newSlot: AvailabilitySlot): string | null => {
  const existingSlots = getAvailabilityForDay(newSlot.dayOfWeek);
  
  for (const existing of existingSlots) {
    // Check for exact duplicate
    if (existing.startTime === newSlot.startTime && 
        existing.endTime === newSlot.endTime && 
        existing.mode === newSlot.mode) {
      return 'This exact time slot already exists for this day.';
    }
    
    // Check for time overlap
    const existingStart = existing.startTime;
    const existingEnd = existing.endTime;
    const newStart = newSlot.startTime;
    const newEnd = newSlot.endTime;
    
    // If times overlap (start before existing ends AND end after existing starts)
    if (newStart < existingEnd && newEnd > existingStart) {
      const formattedExisting = `${formatTime(existingStart)} - ${formatTime(existingEnd)}`;
      return `This time slot overlaps with existing slot: ${formattedExisting}`;
    }
  }
  
  return null; // No conflicts
};

// Actions
const addTimeSlot = () => {
  // Check if Google Calendar is connected
  if (!isGoogleCalendarLinked.value) {
    showGoogleCalendarRequiredModal.value = true;
    return;
  }
  
  logDebug('Opening Add Time Slot modal');
  showModal.value = true;
  // Calculate initial end time when modal opens
  calculateEndTime();
};

const closeModal = () => {
  showModal.value = false;
  // Reset form
  newSlot.value = {
    dayOfWeek: '',
    startTime: '09:00',
    endTime: '10:00',
    mode: 'online'
  };
};

const submitTimeSlot = async () => {
  if (!auth.currentUser) {
    showMessage('Please log in to add time slots', 'error');
    return;
  }

  if (newSlot.value.dayOfWeek && newSlot.value.startTime && newSlot.value.endTime && newSlot.value.mode) {
    // Auto-calculate end time to ensure 1-hour duration
    calculateEndTime();
    
    // Validate that we have a valid start time
    if (!newSlot.value.startTime) {
      showMessage('Please select a start time', 'error');
      return;
    }

    const slot: AvailabilitySlot = {
      dayOfWeek: newSlot.value.dayOfWeek,
      startTime: newSlot.value.startTime,
      endTime: newSlot.value.endTime,
      mode: newSlot.value.mode
    };

    // Check for duplicates or overlaps
    const conflictMessage = checkForDuplicateOrOverlap(slot);
    if (conflictMessage) {
      showMessage(conflictMessage, 'error');
      return;
    }

    availability.value.push(slot);
    
    // Save immediately to Firestore using the efficient single slot method
    try {
      await addSingleSlot(auth.currentUser.uid, slot);
      const formattedTime = `${formatTime(slot.startTime)} - ${formatTime(slot.endTime)}`;
      const modeText = slot.mode === 'both' ? 'Online & Face-to-Face' : 
                       slot.mode === 'face-to-face' ? 'Face-to-Face' : 'Online';
      showMessage(`Availability added successfully! ${slot.dayOfWeek}, ${formattedTime} (${modeText})`, 'success');
      closeModal();
    } catch (error) {
      showMessage('Failed to save time slot. Please try again.', 'error');
      // Remove the slot if save failed
      availability.value.pop();
    }
  }
};

const removeTimeSlot = async (day: string, index: number) => {
  if (!auth.currentUser) {
    showMessage('Please log in to remove time slots', 'error');
    return;
  }

  const daySlots = getAvailabilityForDay(day);
  if (daySlots.length > index) {
    const slot = daySlots[index];
    
    // Check for existing sessions in this time slot
    const hasExistingSessions = await checkForExistingSessions(slot);
    
    // Store the slot info for confirmation dialog
    slotToDelete.value = { day, index, slot, hasExistingSessions };
    showDeleteConfirmation.value = true;
  }
};

const confirmDelete = async () => {
  if (!slotToDelete.value || !auth.currentUser) return;
  
  const { day, index, slot } = slotToDelete.value;
  const daySlots = getAvailabilityForDay(day);
  const globalIndex = availability.value.findIndex(s => s === slot);
  
  if (globalIndex !== -1) {
    availability.value.splice(globalIndex, 1);
    
    // Remove immediately from Firestore using the efficient single slot method
    try {
      await removeSingleSlot(auth.currentUser.uid, slot);
      const formattedTime = `${formatTime(slot.startTime)} - ${formatTime(slot.endTime)}`;
      const modeText = slot.mode === 'both' ? 'Online & Face-to-Face' : 
                       slot.mode === 'face-to-face' ? 'Face-to-Face' : 'Online';
      showMessage(`Availability removed: ${slot.dayOfWeek}, ${formattedTime} (${modeText})`, 'success');
    } catch (error) {
      showMessage('Failed to remove time slot. Please try again.', 'error');
      // Re-add the slot if save failed
      availability.value.splice(globalIndex, 0, slot);
    }
  }
  
  // Close confirmation dialog
  closeDeleteConfirmation();
};

const closeDeleteConfirmation = () => {
  showDeleteConfirmation.value = false;
  slotToDelete.value = null;
};

const showMessage = (text: string, type: 'success' | 'error') => {
  message.value = text;
  messageType.value = type;
  showNotification.value = true;
};

const closeNotification = () => {
  showNotification.value = false;
  message.value = '';
};

// Open Google Calendar OAuth flow
const connectToGoogleCalendar = () => {
  // Close the required modal
  showGoogleCalendarRequiredModal.value = false;
  
  // Trigger Google Calendar OAuth flow
  const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID;
  const REDIRECT_URI = `${window.location.origin}/oauth-callback`;
  
  const scope = [
    'https://www.googleapis.com/auth/calendar',
    'https://www.googleapis.com/auth/calendar.events'
  ].join(' ');
  
  const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?${new URLSearchParams({
    client_id: GOOGLE_CLIENT_ID,
    redirect_uri: REDIRECT_URI,
    response_type: 'code',
    scope: scope,
    access_type: 'offline',
    prompt: 'consent',
    state: JSON.stringify({
      returnUrl: window.location.pathname
    })
  })}`;
  
  window.location.href = authUrl;
};

// Watch for start time changes to auto-calculate end time
watch(() => newSlot.value.startTime, () => {
  if (newSlot.value.startTime) {
    calculateEndTime();
  }
});

// Load availability on mount
const loadAvailability = async () => {
  if (!auth.currentUser) return;
  
  try {
    isLoadingAvailability.value = true;
    const data = await getAvailability(auth.currentUser.uid);
    availability.value = data || [];
  } catch (error) {
    showMessage('Failed to load availability', 'error');
  } finally {
    isLoadingAvailability.value = false;
  }
};

onMounted(async () => {
  await checkClearanceStatus();
  await checkGoogleCalendarConnection();
  await loadAvailability();
});
</script>

<style scoped>
/* Modal backdrop fade transition */
.modal-backdrop-enter-active,
.modal-backdrop-leave-active {
  transition: opacity 0.2s ease;
}

.modal-backdrop-enter-from,
.modal-backdrop-leave-to {
  opacity: 0;
}

/* Modal content animations */
.modal-enter-active {
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.modal-leave-active {
  transition: all 0.25s ease-in;
}

.modal-enter-from {
  opacity: 0;
  transform: scale(0.9) translateY(-20px);
}

.modal-leave-to {
  opacity: 0;
  transform: scale(0.95) translateY(10px);
}

.modal-content {
  animation: modalSlideIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

@keyframes modalSlideIn {
  0% {
    transform: scale(0.8) translateY(-30px);
    opacity: 0;
  }
  100% {
    transform: scale(1) translateY(0);
    opacity: 1;
  }
}

/* Backdrop fade transition (fast and simple) */
.backdrop-enter-active,
.backdrop-leave-active {
  transition: opacity 0.2s ease;
}

.backdrop-enter-from,
.backdrop-leave-to {
  opacity: 0;
}

/* Notification popup animations (only for the content box) */
.notification-enter-active {
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

.notification-leave-active {
  transition: all 0.3s ease-in;
}

.notification-enter-from {
  opacity: 0;
  transform: scale(0.7) translateY(-50px);
}

.notification-leave-to {
  opacity: 0;
  transform: scale(0.9) translateY(20px);
}

.notification-popup {
  animation: popupBounce 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

@keyframes popupBounce {
  0% {
    transform: scale(0.3) translateY(-100px);
    opacity: 0;
  }
  50% {
    transform: scale(1.05) translateY(-10px);
    opacity: 0.8;
  }
  100% {
    transform: scale(1) translateY(0);
    opacity: 1;
  }
}

/* Success icon animation */
.success-icon {
  animation: successPulse 0.6s ease-out 0.2s both;
}

@keyframes successPulse {
  0% {
    transform: scale(0);
    background-color: rgb(220 252 231);
  }
  50% {
    transform: scale(1.2);
    background-color: rgb(187 247 208);
  }
  100% {
    transform: scale(1);
    background-color: rgb(220 252 231);
  }
}

.checkmark {
  stroke-dasharray: 20;
  stroke-dashoffset: 20;
  animation: checkmarkDraw 0.5s ease-out 0.4s both;
}

@keyframes checkmarkDraw {
  to {
    stroke-dashoffset: 0;
  }
}

/* Error icon animation */
.error-icon {
  animation: errorShake 0.5s ease-out 0.2s both;
}

@keyframes errorShake {
  0%, 100% {
    transform: translateX(0);
  }
  10%, 30%, 50%, 70%, 90% {
    transform: translateX(-3px);
  }
  20%, 40%, 60%, 80% {
    transform: translateX(3px);
  }
}

.cross {
  stroke-dasharray: 20;
  stroke-dashoffset: 20;
  animation: crossDraw 0.3s ease-out 0.3s both;
}

@keyframes crossDraw {
  to {
    stroke-dashoffset: 0;
  }
}
</style>